import { Endpoints } from "@/shared/API/APIConfig";
import { baseApi, type ApiResponse } from "./base.api";
import type {
    DraftSupplier,
    GetSuppliersParams,
    GetSuppliersResult,
    Supplier,
    SuppliersListResponse,
    HelpDataResponse,
    SupplierDetailsResponse,
} from "@/app/types/suppliers.types";

function mapApiSupplierToSupplier(api: Supplier): Supplier {
    return {
        id: api.id,
        name: api.name,
        trn: api.trn,
        contract_type: api.contract_type,
        contract_type_trans: api.contract_type_trans,
        start_date: api.start_date,
        end_date: api.end_date,
        doc_url: api.doc_url ?? "",
        status: api.status,
        created_at: api.created_at,
        updated_at: api.updated_at,
        supplier_type: api.supplier_type,
        contract_status: api.contract_status,
        items_count: api.items_count,
        categories: api.categories,
        in_use: api.in_use,
    };
}

export const suppliersApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getSuppliers: builder.query<GetSuppliersResult, GetSuppliersParams>({
            query: ({
                page = 1,
                limit = 10,
                search = "",
                status,
                category_ids,
                contract_type,
                contract_status,
            }) => {
                const queryParams = new URLSearchParams();
                queryParams.set("per_page", String(limit));
                queryParams.set("page", String(page));

                if (search) {
                    queryParams.set("search", search);
                }
                if (status) {
                    queryParams.set("status", status);
                }
                const addArrayParams = (key: string, values?: number[]) => {
                    if (values && values.length > 0) {
                        values.forEach((value, index) => {
                            queryParams.append(`${key}[${index}]`, String(value));
                        });
                    }
                };

                addArrayParams("category_ids", category_ids);
                if (contract_type) {
                    queryParams.set("contract_type", contract_type);
                }
                if (contract_status) {
                    contract_status.forEach((status, index) => {
                        queryParams.append(`contract_status[${index}]`, status);
                    });
                }


                return `${Endpoints.suppliers.getAllSuppliers}?${queryParams.toString()}`;
            },
            transformResponse: (
                response: ApiResponse<SuppliersListResponse>,
            ): GetSuppliersResult => {
                if (!response.success) {
                    throw new Error(response.message || "Failed to load suppliers");
                }

                const suppliersMeta = response.data.suppliers;
                const draftSuppliers: DraftSupplier[] = Array.isArray(
                    response.data.draft_suppliers,
                )
                    ? response.data.draft_suppliers
                    : [];
                return {
                    data: (suppliersMeta.data || []).map(mapApiSupplierToSupplier),
                    draftSuppliers,
                    pagination: {
                        page: suppliersMeta.current_page,
                        limit: suppliersMeta.per_page,
                        total: suppliersMeta.total,
                        totalPages: suppliersMeta.total_pages,
                    },
                };
            },
            providesTags: ["Suppliers"],
        }),

    
        getSupplierById: builder.query<SupplierDetailsResponse, string | number>({
            query: (id) => Endpoints.suppliers.getSupplierById(id),
            transformResponse: (response: ApiResponse<SupplierDetailsResponse>): SupplierDetailsResponse => {
              if (!response.success) {
                throw new Error(response.message || "Failed to load supplier");
              }
              return response.data;
            },
            providesTags: (_result, _error, id) => [{ type: "Supplier", id: String(id) }],
            keepUnusedDataFor: 0,
          }),


        changeSupplierStatus: builder.mutation<
            ApiResponse<Supplier>,
            { supplier_ids: number[]; status: "active" | "inactive" }
        >({
            query: ({ supplier_ids, status }) => ({
                url: `${Endpoints.suppliers.changeStatus}`,
                method: "PUT",
                body: { supplier_ids, status },
            }),
            invalidatesTags: ["Suppliers"],
        }),

        deleteSuppliers: builder.mutation<void, number[]>({
            query: (supplierIds) => {
                const queryParams = new URLSearchParams();
                supplierIds.forEach((id, index) => {
                    queryParams.append(`supplier_ids[${index}]`, String(id));
                });
                return {
                    url: `${Endpoints.suppliers.deleteSuppliers}?${queryParams.toString()}`,
                    method: "DELETE",
                };
            },
            invalidatesTags: ["Suppliers"],
        }),
        getHelpData: builder.query<HelpDataResponse, void>({
            query: () => Endpoints.suppliers.getHelpData,
            transformResponse: (response: ApiResponse<HelpDataResponse>): HelpDataResponse => {
                if (!response.success) {
                    throw new Error(response.message || "Failed to load help data");
                }
                return response.data;
            },
        }),

        createSupplier: builder.mutation<ApiResponse<Supplier>, FormData>({
            query: (formData) => ({
                url: Endpoints.suppliers.createSupplier,
                method: "POST",
                body: formData,
            }),
            invalidatesTags: ["Suppliers"],
        }),

        updateSupplier: builder.mutation<
            ApiResponse<Supplier>,
            { id: string | number; data: FormData }
        >({
            query: ({ id, data }) => ({
                url: Endpoints.suppliers.updateSupplier(id),
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_result, _error, arg) => [
                "Suppliers",
                { type: "Supplier", id: String(arg.id) },
            ],
        }),

        showSupplierDraft: builder.query<ApiResponse<DraftSupplier>, string>({
            query: (id) => `${Endpoints.draft.showSupplierDraft}/${id}`,
            transformResponse: (
                response: ApiResponse<DraftSupplier>,
            ): ApiResponse<DraftSupplier> => {
                return response;
            },
            providesTags: ["SupplierDraft"],
        }),

        updateSupplierDraft: builder.mutation<
            ApiResponse<DraftSupplier>,
            { id: string; data: FormData }
        >({
            query: ({ id, data }) => ({
                url: `${Endpoints.draft.updateSupplierDraft}/${id}`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_result, _error, arg) => {
                const status = arg.data.get("status");
                if (status === "draft") {
                    return ["Suppliers", "SupplierDraft"] as const;
                }
                return ["Suppliers"] as const;
            },
        }),

    }),
});

export const {
    useGetSuppliersQuery,
    useChangeSupplierStatusMutation,
    useGetHelpDataQuery,
    useDeleteSuppliersMutation,
    useCreateSupplierMutation,
    useUpdateSupplierMutation,
    useShowSupplierDraftQuery,
    useUpdateSupplierDraftMutation,
    useGetSupplierByIdQuery,
} = suppliersApi;